/**
 * @fileoverview Страница управления интервью
 * Предоставляет интерфейс для просмотра и управления интервью
 *
 * @author HR Recruiter Team
 * @version 1.0.0
 * @since 2024-01-15
 */

import React from "react";
import toast from 'react-hot-toast';
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecruiterState } from "../utils/recruiterStateManager";
import { useAuthStore } from "../store/authStore";
import { useVacancyStore } from "../store/vacancyStore";
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from "../hooks/useBrandingColors";
import {
  ArrowDownSLineIconV7,
  LinkIconV7,
  Search2LineIconV7,
} from "./ui/figma-icons-v7";
import { QuestionsTab } from "./questions_tab";
import { ContentTabToggle } from "./ui/content-tab-toggle";
import { AssigneeDropdown } from "./ui/assignee-dropdown";
import { VacancyHeader } from "./vacancies/VacancyHeader";
import { InterviewCreationForm } from "./vacancies/InterviewCreationForm";
import { DownloadPdfButton } from "./ui/download-pdf-button";
import {
  VacanciesPageUnifiedProps as InterviewsPageUnifiedProps,
  ContentTab,
  ViewMode,
  StatusTab,
  FilterTab,
  ColumnFilters,
  SortField,
  NavigationTab, InnerInterview,
} from "./vacancies/types";
import {
  defaultSortFieldsOrder,
} from "./vacancies/constants";
import { useAdaptivePageSize } from "../hooks/useAdaptivePageSize";
import {
  getVacancyStatusLabel,
} from "./vacancies/helpers";
import { getUiVacancyStatusDotClass } from "../utils/vacancyStatus";
import { filterCandidatesBySearch } from "../utils/vacancies/filters";
import { sortCandidates } from "../utils/vacancies/sorting";
import { getPageItems } from "../utils/vacancies/pagination";
import { CandidateAnswersPanel } from "./vacancies/CandidateAnswersPanel";
import { CandidatesTable } from "./vacancies/CandidatesTable";
import { apiService } from "../services/apiService";
import { optimizedApiService } from "../services/optimizedApiService";
import { publicApiClient } from "../api/apiClient";
import type {Position as ApiPosition} from "../../generated-src/client/positions/api";
import { PositionStatusEnum } from "../../generated-src/client/positions/api";
import { InterviewStatusEnum } from "../../generated-src/client/interviews/api";
import { InterviewResultEnum } from "../../generated-src/client/interviews/api";


/**
 * Unified Vacancies Page Component
 * Единая структура со всеми остальными страницами - хедер вверху, контент по центру
 */
export function InterviewsPageUnified({
  activeTab = "vacancies",
  onTabChange,
  onCandidateClick,
  onEditVacancy,
  onUserProfileClick,
  onCreateInterview,
}: InterviewsPageUnifiedProps) {
  const colors = useBrandingColors();
  const navigate = useNavigate();
  const { state: recruiterState, refreshVacancies } = useRecruiterState();
  const { user, role } = useAuthStore();

  // Используем выбранную вакансию из глобального store
  const { selectedVacancyId, setSelectedVacancyById, selectedVacancy } = useVacancyStore();

  const [contentTab, setContentTab] = React.useState<ContentTab>("candidates");
  const [viewMode, setViewMode] = React.useState<ViewMode>("list");
  const [copiedInterviewLink, setCopiedInterviewLink] = React.useState(false);

  const [selectedCandidate, setSelectedCandidate] = React.useState<any>(null);
  const [isPdfLoading, setIsPdfLoading] = React.useState(false);
  const [expandedAnswers, setExpandedAnswers] = React.useState<{[key: number]: boolean}>({
    1: false, // Вопрос 1 - свернут
    2: false, // Вопрос 2 - свернут (по умолчанию)
    3: false  // Вопрос 3 - свернут
  });
  // Убрано модальное окно выбора вакансии - теперь сразу переходим к форме

  // Статистика интервью для шапки (всего, завершённых, успешных, провалов)
  const [interviewStats, setInterviewStats] = React.useState<{
    total: number;
    finished: number;
    successful: number;
    failed: number;
  }>({ total: 0, finished: 0, successful: 0, failed: 0 });

  // Состояния для данных интервью
  const [interviewDetails, setInterviewDetails] = React.useState<any>(null);
  const [interviewLoading, setInterviewLoading] = React.useState(false);
  const [interviewError, setInterviewError] = React.useState<string | null>(null);
  const [activeStatusTab, setActiveStatusTab] = React.useState<StatusTab>("all");

  const [filterTab, setFilterTab] = React.useState<FilterTab>("all");
  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = React.useState<PositionStatusEnum | "all">(PositionStatusEnum.ACTIVE);
  const [searchValue, setSearchValue] = React.useState("");
  const [candidatesSearchValue, setCandidatesSearchValue] = React.useState("");

  // Column filters state
  const [columnFiltersOpen, setColumnFiltersOpen] = React.useState(false);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFilters>({
    name: "",
    status: "all",
    rating: "all",
    date: "all"
  });
  const [localFilters, setLocalFilters] = React.useState<ColumnFilters>({
    name: "",
    status: "all",
    rating: "all",
    date: "all"
  });

  // Sort state
  const [sortDropdownOpen, setSortDropdownOpen] = React.useState(false);
  const [sortFieldsOrder, setSortFieldsOrder] = React.useState<SortField[]>(defaultSortFieldsOrder);
  const [draggedItem, setDraggedItem] = React.useState<number | null>(null);

  // Vacancy status dropdown state
  const [vacancyStatusDropdownOpen, setVacancyStatusDropdownOpen] = React.useState(false);
  const [currentVacancyStatus, setCurrentVacancyStatus] = React.useState<"active" | "paused" | "archived">("active");

  // Доступ к управлению исполнителем: все роли
  const canManageAssignee = true;

  // Disable local header: use global header from Layout
  const SHOW_LOCAL_HEADER = false;

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPositions, setTotalPositions] = React.useState(0);
  const candidatesSearchInputRef = React.useRef<HTMLInputElement | null>(null);
  // Адаптивный размер страницы для вакансий
  const vacanciesPageSize = useAdaptivePageSize('vacancies');
  // Пагинация для списка собеседований (кандидатов) — серверная
  const [candidatesCurrentPage, setCandidatesCurrentPage] = React.useState(1);
  const candidatesItemsPerPage = useAdaptivePageSize('interviews');
  const [candidatesTotal, setCandidatesTotal] = React.useState(0);
  console.log('🔍 Pagination(debug): interviews itemsPerPage =', candidatesItemsPerPage);

  // Assignee information state
  const [assignees, setAssignees] = React.useState<Record<number, { firstName: string; lastName: string; email: string }>>({});

  // Create vacancy form state - УБРАНО, перенесено в VacancyCreationForm.tsx





  // Interview form handlers
  const handleInterviewFieldChange = (field: string, value: string) => {
    setInterviewFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const location = useLocation();

  React.useEffect(() => {
    setViewMode("list");
    setSelectedCandidate(null);
    setInterviewDetails(null);
    setInterviewError(null);
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    const handler = () => {
      setViewMode("list");
      setSelectedCandidate(null);
      setInterviewDetails(null);
      setInterviewError(null);
    };
    window.addEventListener('interviewsTabClick', handler);
    return () => window.removeEventListener('interviewsTabClick', handler);
  }, []);

  const convertStatusTabToApiFilters = (statusTab: StatusTab): { status?: string; result?: string } => {
    switch (statusTab) {
      case 'successful':
        return { result: 'SUCCESSFUL' };
      case 'failed':
        return { result: 'UNSUCCESSFUL' };
      case 'finished':
        return { status: 'FINISHED' };
      case 'all':
      default:
        return {};
    }
  };

  const handleCreateInterviewSubmit = async () => {
    if (!selectedVacancyId) {
      return;
    }

    if (!interviewFormData.candidateFirstName || !interviewFormData.candidateLastName || !interviewFormData.candidateEmail) {
      return;
    }

    try {
      // Создаем интервью через API
      const interviewData = {
        positionId: selectedVacancyId,
        candidateEmail: interviewFormData.candidateEmail,
        firstName: interviewFormData.candidateFirstName,
        lastName: interviewFormData.candidateLastName
      };

      console.log('Создание интервью:', interviewData);

      // Вызываем API для создания интервью
      const createdInterview = await apiService.createInterview(interviewData);

      console.log('Интервью создано:', createdInterview);

      // Сбрасываем форму
      setInterviewFormData({
        candidateFirstName: '',
        candidateLastName: '',
        candidateEmail: ''
      });

      // Возвращаемся к списку вакансий
      setViewMode("list");

      // Обновляем данные
      if (selectedVacancyId) {
        // Обновляем список кандидатов для выбранной вакансии
        console.log('Обновляем список кандидатов для вакансии:', selectedVacancyId);

        // Принудительно обновляем список кандидатов
        const refreshCandidates = async () => {
          try {
            const apiFilters = convertStatusTabToApiFilters(activeStatusTab);
            const opts = {
              ...apiFilters,
              search: candidatesSearchValue.trim() || undefined,
            };
            console.log('🔍 refreshCandidates: загружаем интервью для positionId:', selectedVacancyId);
            const { items: interviews, total } = await optimizedApiService.getPositionInterviewsOnDemand(selectedVacancyId, 1, candidatesItemsPerPage, opts);
            console.log('🔍 refreshCandidates: получены интервью:', interviews);
            setCandidatesTotal(total);

            // Логируем структуру первого интервью для анализа
            if (interviews.length > 0) {
              console.log('🔍 refreshCandidates: структура первого интервью:', interviews[0]);
              console.log('🔍 refreshCandidates: все ключи первого интервью:', Object.keys(interviews[0]));
              console.log('🔍 refreshCandidates: candidate в первом интервью:', (interviews[0] as any).candidate);
            }
            const uiInterviews: InnerInterview[] = interviews.map((interview: any) => {
              // В новом API поля candidateFirstName, candidateLastName находятся в объекте интервью
              const score = interview.score || 0;

              const completedAt = interview.finishedAt || interview.createdAt;
              const dt = completedAt ? new Date(completedAt) : null;
              const completed = dt ? `${String(dt.getDate()).padStart(2,'0')}.${String(dt.getMonth()+1).padStart(2,'0')}.${dt.getFullYear()}` : "—";

              const status = ((): string => {
                // Используем статус интервью вместо статуса кандидата
                switch (interview.status) {
                  case InterviewStatusEnum.FINISHED:
                    return interview.result === InterviewResultEnum.SUCCESSFUL ? 'hired' : 'rejected';
                  case InterviewStatusEnum.IN_PROGRESS:
                    return 'in_progress';
                  case InterviewStatusEnum.NOT_STARTED:
                  default:
                    return 'new';
                }
              })();

              const result = interview.result || undefined;
              const interviewStatus = interview.status || undefined;
              const archived = interview.status === InterviewStatusEnum.FINISHED && result === InterviewResultEnum.UNSUCCESSFUL;

              // Имя из полей интервью (в новом API поля называются candidateFirstName, candidateLastName)
              const name = `${interview.candidateLastName || ''} ${interview.candidateFirstName || ''}`.trim() || 'Неизвестный кандидат';

              return {
                id: `${interview.id}_${interview.candidateId}`,
                name,
                email: interview.candidateEmail || '',
                rating: score,
                status,
                completed,
                selected: false,
                result,
                interviewStatus,
                archived,
                interviewId: interview.id,
                candidateId: interview.candidateId,
                priorCommunication: interview.priorCommunication || undefined,
              } as InnerInterview;
            });

            setCandidates(uiInterviews);

            // Обновляем счетчик кандидатов для этой вакансии
            setPositionCandidateCounts(prev => ({
              ...prev,
              [selectedVacancyId]: total
            }));

            // Обновляем статистику интервью в шапке
            try {
              const { items: allInterviews } = await apiService.getPositionInterviewsPaged(selectedVacancyId, 1, 9999);
              let finished = 0, successful = 0, failed = 0;
              for (const iv of allInterviews) {
                if ((iv as any).status === InterviewStatusEnum.FINISHED) {
                  finished++;
                  if ((iv as any).result === InterviewResultEnum.SUCCESSFUL) successful++;
                  if ((iv as any).result === InterviewResultEnum.UNSUCCESSFUL) failed++;
                }
              }
              setInterviewStats({ total: allInterviews.length, finished, successful, failed });
            } catch {}

          } catch (error) {
            console.error('Ошибка обновления списка кандидатов:', error);
          }
        };

        // Принудительно обновляем данные
        await refreshCandidates();

        // Ждем завершения загрузки кандидатов
        while (isLoadingCandidates) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Обновляем счетчики интервью для всех вакансий (оптимизированно)
        const refreshInterviewCounts = async () => {
          try {
            const positionIds = positions.map(p => p.id);
            console.log('🔍 Обновляем счетчики интервью для вакансий (batch):', positionIds);

            // Загружаем только позиции (без интервью)
            const data = await optimizedApiService.getVacanciesPageData({
              search: searchValue || undefined,
              page: currentPage,
              size: vacanciesPageSize,
              owner: filterTab === "my" ? "me" : "all",
              status: selectedStatusFilter === "all" ? undefined : selectedStatusFilter,
            });

            // НЕ загружаем интервью - счетчики будут обновляться при выборе позиций
            const interviewCounts: Record<number, number> = {};

            setPositionCandidateCounts(prev => ({
              ...prev,
              ...interviewCounts
            }));
          } catch (error) {
            console.error('Ошибка обновления счетчиков интервью:', error);
          }
        };

        await refreshInterviewCounts();
      }

    } catch (error) {
      console.error('Ошибка создания интервью:', error);
    }
  };

  // API data state
  const [positions, setPositions] = React.useState<ApiPosition[]>([]);

  // Interview creation form state
  const [interviewFormData, setInterviewFormData] = React.useState({
    candidateFirstName: '',
    candidateLastName: '',
    candidateEmail: ''
  });
  // selectedVacancyId теперь берется из vacancyStore (selectedVacancyId)
  
  // Состояние для кэшированных данных позиций
  const [positionDataCache, setPositionDataCache] = React.useState<{
    interviews: Record<number, any[]>;
    questions: Record<number, any[]>;
  }>({
    interviews: {},
    questions: {}
  });

  // Логирование состояния для отладки
  console.log('🔍 InterviewsPageUnified: Рендерится с viewMode =', viewMode);
  console.log('🔍 statusDropdownOpen =', statusDropdownOpen);
  console.log('🔍 selectedVacancyId =', selectedVacancyId);

  // Отслеживаем изменения statusDropdownOpen
  React.useEffect(() => {
    console.log(`🔍 statusDropdownOpen изменился на: ${statusDropdownOpen}`);
  }, [statusDropdownOpen]);

  // Загружаем данные позиции при её выборе (ленивая загрузка позиций/вопросов)
  React.useEffect(() => {
    if (!selectedVacancyId) return;

    const loadPositionData = async () => {
      console.log('🔄 Загружаем данные для позиции:', selectedVacancyId);
      
      // Проверяем кэш
      const cachedInterviews = positionDataCache.interviews[selectedVacancyId];
      const cachedQuestions = positionDataCache.questions[selectedVacancyId];

      // Кэш считаем валидным, если массивы уже присутствуют (даже пустые)
      if (Array.isArray(cachedInterviews) && Array.isArray(cachedQuestions)) {
        console.log('✅ Данные уже в кэше для позиции (валидный кэш):', selectedVacancyId);
        return;
      }

      try {
        // Загружаем вопросы; интервью теперь грузим постранично ниже
        const questions = await optimizedApiService.getPositionQuestionsOnDemand(selectedVacancyId);

        // Обновляем кэш только вопросов
        setPositionDataCache(prev => ({
          interviews: { ...prev.interviews },
          questions: { ...prev.questions, [selectedVacancyId]: questions }
        }));

        // Обновляем questionsCount в списке позиций
        setPositions(prev => prev.map(p => (
          p.id === selectedVacancyId ? { ...p, questionsCount: questions?.length ?? 0 } : p
        )));

        console.log('✅ Загружены вопросы для позиции:', selectedVacancyId, { questions: questions.length });
      } catch (error) {
        console.error('❌ Ошибка загрузки данных позиции:', selectedVacancyId, error);
      }
    };

    loadPositionData();
  }, [selectedVacancyId]);





  // Загружаем выбранную вакансию, если её нет в списке (например, неактивная)
  React.useEffect(() => {
    const loadSelectedVacancy = async () => {
      // Если есть ID, но нет вакансии в store, и её нет в локальном списке
      if (selectedVacancyId && !selectedVacancy && !positions.find(p => p.id === selectedVacancyId)) {
        setSelectedVacancyById(selectedVacancyId);
      }
    };

    loadSelectedVacancy();
  }, [selectedVacancyId, selectedVacancy, positions, setSelectedVacancyById]);

  // Используем selectedVacancy из store (может содержать неактивные вакансии)
  // Если не найдена в store, пытаемся найти в локальном списке
  const selectedPosition = React.useMemo(() => {
    const result = selectedVacancy || positions.find(p => p.id === selectedVacancyId) || null;
    console.log('🔍 VacanciesPage selectedPosition calc:', {
      selectedVacancyId,
      hasSelectedVacancy: !!selectedVacancy,
      selectedVacancyTitle: selectedVacancy?.title,
      foundInPositions: !!positions.find(p => p.id === selectedVacancyId),
      resultTitle: result?.title,
      result
    });
    return result;
  }, [selectedVacancy, positions, selectedVacancyId]);
  const [candidates, setCandidates] = React.useState<InnerInterview[]>([]);
  const [positionCandidateCounts, setPositionCandidateCounts] = React.useState<Record<number, number>>({});

  const handleCandidateSelect = (candidateId: string) => {
    setCandidates((prev) =>
      prev.map((candidate) =>
        candidate.id === candidateId
          ? { ...candidate, selected: !candidate.selected }
          : candidate,
      ),
    );
  };

  const handlePositionStatusChange = async (positionId: number, newStatus: PositionStatusEnum) => {
    try {
      console.log(`🔄 Изменение статуса вакансии ${positionId} на ${newStatus}`);
      console.log(`🔍 Тип positionId:`, typeof positionId);
      console.log(`🔍 Тип newStatus:`, typeof newStatus);

      // Обновляем статус через API
      console.log(`📡 Отправляем запрос к API...`);
      const result = await apiService.updatePositionStatus(positionId, newStatus);
      console.log(`📡 Ответ от API:`, result);

      // Обновляем локальное состояние
      console.log(`🔄 Обновляем локальное состояние...`);
      setPositions(prev => {
        console.log(`🔍 Предыдущее состояние:`, prev);
        const updated = prev.map(pos =>
          pos.id === positionId ? { ...pos, status: newStatus } : pos
        );
        console.log(`🔍 Обновленное состояние:`, updated);
        return updated;
      });

      console.log(`✅ Статус вакансии ${positionId} успешно изменен на ${newStatus}`);

      // Обновляем список вакансий для учета изменений в фильтрации
      console.log(`🔄 Обновляем список вакансий через refreshVacancies...`);
      refreshVacancies();

      // Показываем уведомление об успехе
      // TODO: Добавить toast уведомление

    } catch (error: any) {
      console.error(`❌ Ошибка изменения статуса вакансии ${positionId}:`, error);
      console.error(`❌ Детали ошибки:`, error.response?.data, error.message);

      // Показываем уведомление об ошибке
      // TODO: Добавить toast уведомление

      // Можно добавить откат изменений в UI
    }
  };

  // Функция для изменения фильтра по статусу
  const handleStatusFilterChange = (newStatus: PositionStatusEnum | "all") => {
    console.log(`🔍 handleStatusFilterChange вызван с newStatus=${newStatus}`);

    // Обновляем фильтр
    setSelectedStatusFilter(newStatus);
    setStatusDropdownOpen(false);

    console.log(`✅ Фильтр по статусу обновлен на: ${newStatus}`);
  };



  // Helper: map API enums to UI strings
  const mapPositionStatusToUi = (status?: PositionStatusEnum) => {
    switch (status) {
      case PositionStatusEnum.ACTIVE: return "active" as const;
      case PositionStatusEnum.PAUSED: return "paused" as const;
      case PositionStatusEnum.ARCHIVED: return "archived" as const;
      default: return "active" as const;
    }
  };

  // Функция для загрузки информации об исполнителях вакансий
  const loadAssignees = async (positions: any[]) => {
    try {
      const assigneeIds = positions
        .map(p => Number(p.managerId))
        .filter((id): id is number => !Number.isNaN(id));

      if (assigneeIds.length === 0) return;

      console.log('🔍 Загружаем информацию об исполнителях:', assigneeIds);

      // Один запрос для получения всех пользователей
      // Важно: сигнатура (search, role, companyId, page, size, sort)
      const allUsers = await publicApiClient.account.listAccounts(undefined, undefined, 'ACTIVE' as any, undefined, 0, 1000);
      const assigneesMap: Record<number, { firstName: string; lastName: string; email: string }> = {};
      
      // Создаем мапу пользователей по ID (нормализуем ключ к number)
      const usersMap = new Map<number, any>();
      allUsers.data.content?.forEach((user: any) => {
        const key = Number(user.id);
        if (!Number.isNaN(key)) {
          usersMap.set(key, user);
        }
      });
      
      // Заполняем данные assignees только для нужных ID
      assigneeIds.forEach((userId) => {
        const user = usersMap.get(Number(userId));
        if (user) {
          assigneesMap[userId] = {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || ''
          };
        }
      });

      setAssignees(assigneesMap);
      console.log('🔍 Информация об исполнителях загружена:', assigneesMap);
    } catch (e) {
      console.error('Ошибка загрузки информации об исполнителях:', e);
    }
  };

  // Load positions from API (paged)
  React.useEffect(() => {
    console.log('🔍 VacanciesPage: useEffect для загрузки вакансий сработал, refreshFlags.vacancies =', recruiterState?.refreshFlags?.vacancies);
    let cancelled = false;
    const load = async () => {
      try {
        const data = await optimizedApiService.getVacanciesPageData({
          search: searchValue || undefined,
          page: currentPage,
          size: vacanciesPageSize,
          owner: filterTab === "my" ? "me" : "all",
          status: selectedStatusFilter === "all" ? undefined : selectedStatusFilter,
        });
        if (cancelled) return;

        console.log('🔍 API Positions Response:', data);
        console.log('🔍 Positions items:', data.positions);

        const positions = data.positions || [];
        setPositions(positions);
        setTotalPositions(data.total || 0);

        // Автовыбор первой вакансии управляется через isFirstLoad в useVacanciesData
        // Не сбрасываем выбранную вакансию, даже если она не в списке (может быть неактивной)

        // Убрана массовая загрузка counts вопросов — вопросы грузятся лениво при выборе вакансии

        // Загружаем информацию об исполнителях
        await loadAssignees(positions);
      } catch (e: any) {
        console.error('🔍 Ошибка загрузки вакансий:', e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [searchValue, currentPage, filterTab, selectedStatusFilter, recruiterState?.refreshFlags?.vacancies]);

  // Update current vacancy status when selection changes
  React.useEffect(() => {
    setCurrentVacancyStatus(mapPositionStatusToUi(selectedPosition?.status));
  }, [selectedPosition]);

  // Убрана массовая загрузка счетчиков интервью — интервью загружаются только при выборе вакансии

  // Load candidates for selected position from server with pagination
  const [isLoadingCandidates, setIsLoadingCandidates] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!selectedVacancyId) { setCandidates([]); return; }

      setIsLoadingCandidates(true);
      try {
        const apiFilters = convertStatusTabToApiFilters(activeStatusTab);
        const opts = {
          ...apiFilters,
          search: candidatesSearchValue.trim() || undefined,
        };

        console.log('🔍 Loading interviews for position (server paged with filters):', { 
          selectedVacancyId, 
          page: candidatesCurrentPage, 
          size: candidatesItemsPerPage,
          filters: opts
        });

        const { items: interviews, total } = await optimizedApiService.getPositionInterviewsOnDemand(
          selectedVacancyId, 
          candidatesCurrentPage, 
          candidatesItemsPerPage,
          opts
        );

        if (cancelled) return;

        // Каждое интервью - отдельная строка в таблице
        const uiCandidates: InnerInterview[] = interviews.map((interview: any) => {
          // В новом API поля candidateFirstName, candidateLastName находятся в объекте InterviewListItem
          const score = interview.score || 0;

          // Дата завершения - только для завершенных интервью
          const completedAt = interview.finishedAt;
          const dt = completedAt ? new Date(completedAt) : null;
          const completed = dt ? `${String(dt.getDate()).padStart(2,'0')}.${String(dt.getMonth()+1).padStart(2,'0')}.${dt.getFullYear()}` : "—";

          // Статус кандидата (на основе интервью)
          const status = ((): string => {
            switch (interview.status) {
              case InterviewStatusEnum.FINISHED:
                return interview.result === InterviewResultEnum.SUCCESSFUL ? 'hired' : 'rejected';
              case InterviewStatusEnum.IN_PROGRESS:
                return 'in_progress';
              case InterviewStatusEnum.NOT_STARTED:
              default:
                return 'new';
            }
          })();

          const result = interview.result || undefined;
          const interviewStatus = interview.status || undefined;
          const archived = interview.status === InterviewStatusEnum.FINISHED && result === InterviewResultEnum.UNSUCCESSFUL;

          // Логируем данные интервью для отладки
          console.log('🔍 refreshCandidates: маппинг интервью:', {
            interviewId: interview.id,
            interviewCandidateFirstName: interview.candidateFirstName,
            interviewCandidateLastName: interview.candidateLastName,
            interviewEmail: 'N/A', // Email не включен в InterviewListItem
            interviewName: `${interview.candidateLastName || ''} ${interview.candidateFirstName || ''}`.trim()
          });

          // Имя из полей интервью (в новом API поля называются candidateFirstName, candidateLastName)
          const name = `${interview.candidateLastName || ''} ${interview.candidateFirstName || ''}`.trim() || 'Неизвестный кандидат';

          console.log('🔍 Сформированное имя:', name);

          return {
            id: `${interview.id}_${interview.candidateId}`,
            name,
            email: interview.candidateEmail || '',
            rating: score,
            status,
            completed,
            selected: false,
            result,
            interviewStatus,
            archived,
            // Дополнительные поля для отладки
            interviewId: interview.id,
            candidateId: interview.candidateId,
            priorCommunication: interview.priorCommunication || undefined,
          } as InnerInterview;
        });

        console.log('🔍 Mapped UI candidates:', uiCandidates);
        
        setCandidatesTotal(total);
        setCandidates(uiCandidates);
      } catch (e) {
        console.error('🔍 Error loading candidates:', e);
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setIsLoadingCandidates(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedVacancyId, candidatesCurrentPage, candidatesItemsPerPage, activeStatusTab, candidatesSearchValue]);

  // Загрузка статистики интервью для шапки (один запрос без фильтров)
  React.useEffect(() => {
    if (!selectedVacancyId) {
      setInterviewStats({ total: 0, finished: 0, successful: 0, failed: 0 });
      return;
    }
    let cancelled = false;
    const loadStats = async () => {
      try {
        // Один запрос: все интервью позиции, size=9999 чтобы получить все элементы
        const { items } = await apiService.getPositionInterviewsPaged(selectedVacancyId, 1, 9999);
        if (cancelled) return;
        let finished = 0, successful = 0, failed = 0;
        for (const iv of items) {
          if ((iv as any).status === InterviewStatusEnum.FINISHED) {
            finished++;
            if ((iv as any).result === InterviewResultEnum.SUCCESSFUL) successful++;
            if ((iv as any).result === InterviewResultEnum.UNSUCCESSFUL) failed++;
          }
        }
        setInterviewStats({ total: items.length, finished, successful, failed });
      } catch (e) {
        console.error('Ошибка загрузки статистики интервью:', e);
      }
    };
    loadStats();
    return () => { cancelled = true; };
  }, [selectedVacancyId]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((columnFiltersOpen || sortDropdownOpen || vacancyStatusDropdownOpen || statusDropdownOpen) && event.target instanceof Element) {
        const filtersDropdown = event.target.closest('[data-dropdown="column-filters"]');
        const sortDropdown = event.target.closest('[data-dropdown="sort"]');
        const statusDropdown = event.target.closest('[data-dropdown="vacancy-status"]');
        const statusFilterDropdown = event.target.closest('[data-dropdown="status-filter"]');

        if (columnFiltersOpen && !filtersDropdown) {
          setColumnFiltersOpen(false);
        }
        if (sortDropdownOpen && !sortDropdown) {
          setSortDropdownOpen(false);
        }
        if (vacancyStatusDropdownOpen && !statusDropdown) {
          setVacancyStatusDropdownOpen(false);
        }
        if (statusDropdownOpen && !statusFilterDropdown) {
          setStatusDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnFiltersOpen, sortDropdownOpen, vacancyStatusDropdownOpen, statusDropdownOpen]);

  // Paginated vacancies (from API)
  const totalPages = Math.ceil((totalPositions || 0) / vacanciesPageSize);
  const paginatedPositions = positions;

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterTab, searchValue, selectedStatusFilter]);

  // Sort candidates (фильтрация теперь выполняется на сервере)
  const filteredAndSortedCandidates = React.useMemo(() => {
    // Приводим типы к совместимости
    const compatibleSortFields = sortFieldsOrder.map(sf => ({
      field: sf.key || 'name',
      direction: sf.direction || 'asc'
    }));
    return sortCandidates(candidates, compatibleSortFields);
  }, [candidates, sortFieldsOrder]);

  // Сбрасываем страницу кандидатов при изменении фильтров/поиска/сортировки
  React.useEffect(() => {
    setCandidatesCurrentPage(1);
  }, [candidatesSearchValue, activeStatusTab, columnFilters, sortFieldsOrder]);

  const candidatesTotalPages = Math.max(1, Math.ceil(candidatesTotal / candidatesItemsPerPage));
  console.log('🔍 Pagination(debug): totalCandidates(server) =', candidatesTotal, 'totalPages =', candidatesTotalPages);
  const pagedCandidates = filteredAndSortedCandidates;

  const handleSelectAllCandidates = React.useCallback(() => {
    const allSelectedOnPage = pagedCandidates.length > 0 && pagedCandidates.every(candidate => candidate.selected);
    const pageIds = new Set(pagedCandidates.map(c => c.id));
    setCandidates((prev) =>
      prev.map((candidate) => {
        if (pageIds.has(candidate.id)) {
          return { ...candidate, selected: !allSelectedOnPage };
        }
        return candidate;
      }),
    );
  }, [pagedCandidates]);

  const areAllCandidatesSelected = React.useMemo(() => {
    return pagedCandidates.length > 0 && pagedCandidates.every(candidate => candidate.selected);
  }, [pagedCandidates]);



  const handleEditVacancy = () => {
    if (!selectedPosition) {
      console.warn('🔍 handleEditVacancy: нет выбранной вакансии для редактирования');
      return;
    }

    console.log('🔍 handleEditVacancy: переходим к редактированию вопросов интервью:', selectedPosition);

    // Переходим на страницу редактирования вопросов интервью
    navigate(`/recruiter/vacancy/edit-questions/${selectedPosition.id}`);
  };



  const handleCandidateClick = async (candidate: any) => {
    console.log('🔍 handleCandidateClick: клик на кандидата:', candidate);
    setSelectedCandidate(candidate);
    setViewMode("candidate-answers");

    // Загружаем детали интервью
    if (candidate.interviewId) {
      try {
        setInterviewLoading(true);
        setInterviewError(null);

        const details = await apiService.getInterviewAnswers(candidate.interviewId);
        console.log('🔍 Interview answers details:', details);

        // Дополнительно загружаем метаданные интервью (создано/завершено, email и т.п.)
        let merged = details;
        try {
          const interviewMeta = await apiService.getInterview(candidate.interviewId);
          console.log('🔍 Interview meta:', interviewMeta);
          merged = {
            ...details,
            interview: {
              ...(details?.interview || {}),
              createdAt: details?.interview?.createdAt || interviewMeta?.createdAt,
              finishedAt: details?.interview?.finishedAt || interviewMeta?.finishedAt,
            },
            candidate: {
              ...(details?.candidate || {}),
              firstName: details?.candidate?.firstName || interviewMeta?.candidateFirstName || candidate?.firstName,
              lastName: details?.candidate?.lastName || interviewMeta?.candidateLastName || candidate?.lastName,
              email: details?.candidate?.email || interviewMeta?.candidateEmail || interviewMeta?.email || candidate?.email || candidate?.candidateEmail,
            },
          };
        } catch (metaErr) {
          // Мета недоступна — продолжаем с тем, что есть
          console.warn('⚠️ Не удалось загрузить метаданные интервью:', metaErr);
        }

        setInterviewDetails(merged);
      } catch (err) {
        console.error('Error loading interview details:', err);
        setInterviewError('Ошибка загрузки данных интервью');
      } finally {
        setInterviewLoading(false);
      }
    }
  };

  // Вспомогательные функции для форматирования данных
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      });
    } catch (e) {
      return '—';
    }
  };





  const toggleAnswer = (questionNumber: number) => {
    setExpandedAnswers(prev => ({
      ...prev,
      [questionNumber]: !prev[questionNumber]
    }));
  };

  // Функции для работы с тегами - УБРАНЫ, перенесены в VacancyCreationForm.tsx

  // Функция moveQuestion - УБРАНА, перенесена в VacancyCreationForm.tsx





  // Функция handleAddQuestionManually - УБРАНА, перенесена в VacancyCreationForm.tsx



  // Drag handlers for sort dropdown
  const handleDragStart = (index: number) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // QuestionCard Component - УБРАН, перенесен в VacancyCreationForm.tsx
  // Create Vacancy Form Component - УБРАН, перенесен в VacancyCreationForm.tsx










  return (
    <DndProvider backend={HTML5Backend}>
      <div className="relative p-4 pt-2">
        <div className="relative">
                      <div className="box-border content-start flex gap-4 items-stretch justify-start p-0 relative w-full">
              {/* Локальный хедер отключён — используется глобальный из Layout */}
              {SHOW_LOCAL_HEADER && (
                <VacancyHeader
                  activeTab={activeTab}
                  onTabChange={onTabChange ? (tab: string) => onTabChange(tab as NavigationTab) : undefined}
                  onUserProfileClick={onUserProfileClick}
                />
              )}

              {/* Центрирующий контейнер для контента (ширина и отступы задаёт Layout) */}
              <div className="w-full">
                {/* Main Content - На всю ширину */}
                <div className="box-border content-stretch flex flex-col gap-4 items-stretch justify-start p-0 relative w-full">
                {/* DEBUG: viewMode = {viewMode} */}
                {/* Условный рендеринг контента правой колонки */}
                {viewMode === "create-interview" ? (
                  // Форма создания интервью в правой колонке
                  (() => {
                    console.log('🔍 RENDERING: Показываем форму создания интервью');
                    return (
                      <InterviewCreationForm
                        selectedPositionId={selectedVacancyId}
                        interviewFormData={interviewFormData}
                        handleInterviewFieldChange={handleInterviewFieldChange}
                        handleCreateInterviewSubmit={handleCreateInterviewSubmit}
                        onBack={() => setViewMode("list")}
                      />
                    );
                  })()
                ) : viewMode === "candidate-answers" ? (
                  <CandidateAnswersPanel
                    selectedCandidate={selectedCandidate}
                    selectedPositionId={selectedVacancyId}
                    selectedPositionTitle={selectedPosition?.title}
                    selectedPosition={selectedPosition}
                    interviewLoading={interviewLoading}
                    interviewError={interviewError}
                    interviewDetails={interviewDetails}
                    expandedAnswers={expandedAnswers}
                    formatDate={formatDate}
                    onRetry={() => handleCandidateClick(selectedCandidate)}
                    onCreateInterview={() => {
                                      if (!selectedVacancyId) {
                                        alert('Сначала выберите вакансию из списка слева');
                                        return;
                                      }
                                      setViewMode("create-interview");
                                    }}
                    onToggleAnswer={toggleAnswer}
                    onBack={() => setViewMode("list")}
                  />
                ) : (
                  // Детали вакансии в правой колонке
                  <>
                    {/* Первая карточка - Информация о вакансии */}
                    <div className=" box-border content-stretch flex flex-col gap-2 items-start justify-start mt-[15px] px-1 relative rounded-[24px] shrink-0 w-full">
                    <div className="box-border content-stretch flex flex-row gap-2 items-center justify-between p-0 relative shrink-0 w-full">
                    {selectedPosition?.title && (
                       <h1 className="text-2xl ml-2 font-semibold text-[#0a0d14]">
                          Данные об интервью
                     </h1>
                    )}

                    <div className="hidden box-border content-stretch flex flex-row gap-2 items-center justify-end min-h-px min-w-px p-0 relative shrink-0">
                      <DownloadPdfButton
                        onClick={() => {
                          if (!selectedVacancyId) {
                            alert('Сначала выберите вакансию из списка слева');
                            return;
                          }
                          setViewMode("create-interview");
                        }}
                        className="rounded-2xl"
                        label="Повторное интервью"
                        size="medium"
                        icon={null}
                      />
                      <button
                        onClick={() => {
                          // Функциональность ссылки на интервью
                          const candidateBaseUrl = process.env.REACT_APP_CANDIDATE_BASE_URL || '/candidate';
                          const interviewLink = `${candidateBaseUrl}/candidate/interview/${selectedVacancyId}`;

                          // Копируем ссылку в буфер обмена
                          navigator.clipboard.writeText(interviewLink).then(() => {
                            toast.success('Ссылка скопирована');
                            setCopiedInterviewLink(true);
                            setTimeout(() => setCopiedInterviewLink(false), 2000);
                          }).catch(() => {
                            // Fallback если clipboard API недоступен
                            const textArea = document.createElement('textarea');
                            textArea.value = interviewLink;
                            document.body.appendChild(textArea);
                            textArea.select();
                            document.execCommand('copy');
                            document.body.removeChild(textArea);
                            toast.success('Ссылка скопирована');
                            setCopiedInterviewLink(true);
                            setTimeout(() => setCopiedInterviewLink(false), 2000);
                          });

                          console.log('Interview link generated:', interviewLink);
                        }}
                        className="bg-[#F6F8FA] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip px-3 py-2.5 relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors min-w-[180px]"
                      >
                        <LinkIconV7 color={colors.primary} />
                        <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
                          <div className="css-bbeeyw font-inter font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-center text-nowrap">
                            <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                              {copiedInterviewLink ? 'Скопировано' : 'Ссылка на интервью'}
                            </p>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={handleEditVacancy}
                        className="bg-[#F6F8FA] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip px-3 py-2.5 relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors"
                      >
                        <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
                          <div className="css-bbeeyw font-inter font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-center text-nowrap">
                            <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                              Редактировать
                            </p>
                          </div>
                        </div>
                      </button>

                      <div className="relative" data-dropdown="vacancy-status">
                        <button
                          onClick={() => setVacancyStatusDropdownOpen(!vacancyStatusDropdownOpen)}
                          className="bg-[#ffffff] box-border content-stretch flex flex-row gap-2 items-center justify-center overflow-clip h-11 px-3 relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors"
                        >
                          <div className={`w-3 h-3 rounded-full ${getUiVacancyStatusDotClass(currentVacancyStatus)}`} />

                          <div className="font-inter font-medium text-[#525866] text-[14px] text-center text-nowrap">
                            {getVacancyStatusLabel(currentVacancyStatus)}
                          </div>

                          <ArrowDownSLineIconV7 size={24} className={`w-6 h-6 ${vacancyStatusDropdownOpen ? "rotate-180" : ""}`} />
                        </button>

                        {vacancyStatusDropdownOpen && (
                          <div className="absolute top-full right-0 mt-2 w-[180px] bg-[#ffffff] rounded-3xl border border-[#cdd0d5] shadow-lg z-[99999] p-2">
                            <div className="flex flex-col gap-1">
                              {["active", "paused", "archived"].map((status) => (
                                <button
                                  key={status}
                                  onClick={() => {
                                    console.log(`🔍 Клик по статусу ${status} в правой панели`);
                                    setCurrentVacancyStatus(status as any);
                                    setVacancyStatusDropdownOpen(false);

                                    // Вызываем API для изменения статуса
                                    if (selectedVacancyId) {
                                      const apiStatus = status === 'active' ? PositionStatusEnum.ACTIVE :
                                                      status === 'paused' ? PositionStatusEnum.PAUSED :
                                                      PositionStatusEnum.ARCHIVED;
                                      handlePositionStatusChange(selectedVacancyId, apiStatus);
                                    }
                                  }}
                                  className={`flex items-center gap-3 px-4 py-3 rounded-2xl font-['Inter:Medium',_sans-serif] font-medium text-[16px] transition-colors hover:bg-[#f6f8fa] ${
                                    currentVacancyStatus === status 
                                      ? "bg-[#f6f8fa]" 
                                      : "text-[#525866]"
                                  }`}
                                  style={currentVacancyStatus === status ? { color: colors.primary } : undefined}
                                >
                                  <div className={`w-3 h-3 rounded-full ${getUiVacancyStatusDotClass(status as any)}`} />

                                  {getVacancyStatusLabel(status as any)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 bg-[#ffffff] relative rounded-xl shrink-0 w-full">
                    <div className="relative size-full">
                      <div className="box-border content-stretch flex flex-col gap-6 items-start justify-start px-[24px] py-[18px] relative w-full">
                        <div className="box-border content-stretch flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
                          {/* Левая колонка - основные поля (2/3 ширины) */}
                          <div className="box-border content-stretch flex flex-col gap-4 items-start justify-start p-0 relative shrink-0 w-2/3">
                            {/* Первая строка - 3 поля в ряд */}
                            <div className="box-border content-stretch flex flex-row gap-8 items-center p-0 relative shrink-0">
                              {/* Создано */}
                              <div className="box-border content-stretch flex flex-row items-center gap-2 p-0 relative shrink-0">
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[22px] text-nowrap whitespace-pre">
                                    Создано
                                  </p>
                                </div>
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#000000] text-[15px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[23px] text-nowrap whitespace-pre">
                                    {selectedPosition?.createdAt ? formatDate(selectedPosition.createdAt) : '—'}
                                  </p>
                                </div>
                              </div>

                              {/* Минимальный балл */}
                              <div className="box-border content-stretch flex flex-row items-center gap-2 p-0 relative shrink-0">
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                                    Минимальный балл
                                  </p>
                                </div>
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#000000] text-[15px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                                    {selectedPosition?.minScore ?? '—'}
                                  </p>
                                </div>
                              </div>

                              {/* Средний балл */}
                              <div className="box-border content-stretch flex flex-row items-center gap-2 p-0 relative shrink-0">
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                                    Средний балл
                                  </p>
                                </div>
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#000000] text-[15px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[24px] text-nowrap whitespace-pre">
                                    {selectedPosition?.averageScore == null ? '—' : (isFinite(Number(selectedPosition.averageScore)) ? Number(selectedPosition.averageScore).toFixed(1) : '—')}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Вторая строка — статистика интервью */}
                            <div className="box-border content-stretch flex flex-row gap-8 items-center p-0 relative shrink-0">
                              {/* Всего */}
                              <div className="box-border content-stretch flex flex-row items-center gap-2 p-0 relative shrink-0">
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[22px] text-nowrap whitespace-pre">
                                    Всего
                                  </p>
                                </div>
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#000000] text-[15px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[23px] text-nowrap whitespace-pre">
                                    {interviewStats.total}
                                  </p>
                                </div>
                              </div>

                              {/* Завершённых */}
                              <div className="box-border content-stretch flex flex-row items-center gap-2 p-0 relative shrink-0">
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[22px] text-nowrap whitespace-pre">
                                    Завершённых
                                  </p>
                                </div>
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#000000] text-[15px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[23px] text-nowrap whitespace-pre">
                                    {interviewStats.finished}
                                  </p>
                                </div>
                              </div>

                              {/* Успешных */}
                              <div className="box-border content-stretch flex flex-row items-center gap-2 p-0 relative shrink-0">
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[22px] text-nowrap whitespace-pre">
                                    Успешных
                                  </p>
                                </div>
                                <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#38c793] text-[15px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[23px] text-nowrap whitespace-pre">
                                    {interviewStats.successful}
                                  </p>
                                </div>
                              </div>

                              {/* Провалов */}
                              <div className="box-border content-stretch flex flex-row items-center gap-2 p-0 relative shrink-0">
                                <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[22px] text-nowrap whitespace-pre">
                                    Провалов
                                  </p>
                                </div>
                                <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#df1c41] text-[15px] text-left text-nowrap tracking-[-0.176px]">
                                  <p className="adjustLetterSpacing block leading-[23px] text-nowrap whitespace-pre">
                                    {interviewStats.failed}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Правая колонка - кнопки действий */}
                          <div className="box-border content-stretch flex flex-row gap-2 items-start justify-end p-0 relative w-1/3">
                            <div className="box-border content-stretch flex flex-row gap-2 items-center justify-end p-0 relative shrink-0">
                              <DownloadPdfButton
                                onClick={() => {
                                  if (!selectedVacancyId) {
                                    alert('Сначала выберите вакансию из списка слева');
                                    return;
                                  }
                                  setViewMode("create-interview");
                                }}
                                className="rounded-2xl"
                                label="Повторное интервью"
                                size="medium"
                                icon={null}
                              />
                              <button
                                onClick={() => {
                                  const candidateBaseUrl = process.env.REACT_APP_CANDIDATE_BASE_URL || '/candidate';
                                  const interviewLink = `${candidateBaseUrl}/candidate/interview/${selectedVacancyId}`;

                                  navigator.clipboard.writeText(interviewLink).then(() => {
                                    toast.success('Ссылка скопирована');
                                    setCopiedInterviewLink(true);
                                    setTimeout(() => setCopiedInterviewLink(false), 2000);
                                  }).catch(() => {
                                    const textArea = document.createElement('textarea');
                                    textArea.value = interviewLink;
                                    document.body.appendChild(textArea);
                                    textArea.select();
                                    document.execCommand('copy');
                                    document.body.removeChild(textArea);
                                    toast.success('Ссылка скопирована');
                                    setCopiedInterviewLink(true);
                                    setTimeout(() => setCopiedInterviewLink(false), 2000);
                                  });

                                  console.log('Interview link generated:', interviewLink);
                                }}
                                className="bg-[#F6F8FA] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip px-3 py-2.5 relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors min-w-[180px]"
                              >
                                <LinkIconV7 color={colors.primary} />
                                <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
                                  <div className="css-bbeeyw font-inter font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-center text-nowrap">
                                    <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                                      {copiedInterviewLink ? 'Скопировано' : 'Ссылка на интервью'}
                                    </p>
                                  </div>
                                </div>
                              </button>

                              <button
                                onClick={handleEditVacancy}
                                className="bg-[#F6F8FA] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip px-3 py-2.5 relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors"
                              >
                                <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
                                  <div className="css-bbeeyw font-inter font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-center text-nowrap">
                                    <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                                      Редактировать
                                    </p>
                                  </div>
                                </div>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  </div>

                  {/* Вторая карточка - Табы с контентом */}
                  {/* <div className="bg-[#f5f6f1] relative rounded-[24px] shrink-0 w-full flex-1 overflow-hidden"> */}
                    {/* <div className="flex flex-col items-start relative size-full"> */}
                      <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start px-1 relative w-full h-full">
                        {/* Content Toggle and Buttons */}
                        <div className="flex justify-between items-center w-full">
                          <ContentTabToggle
                            options={[
                              { id: "candidates", label: "Собеседования" },
                              { id: "questions", label: "Вопросы собеседования" },
                            ]}
                            activeTab={contentTab}
                            onTabChange={(tab) => {
                              console.log('🔍 ContentTabToggle onTabChange:', { from: contentTab, to: tab });
                              setContentTab(tab);
                            }}
                          />
                          <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start p-0 relative shrink-0">
                            <DownloadPdfButton
                              onClick={async () => {
                                if (isPdfLoading) return;
                                try {
                                  setIsPdfLoading(true);
                                  console.log('Скачивание PDF отчета списка собеседований для позиции:', selectedVacancyId);
                                  await apiService.downloadInterviewsListPDF(
                                    {
                                      positionId: selectedVacancyId,
                                      maxRows: 1000
                                    },
                                    `candidates-list-${selectedVacancyId || 'all'}-${new Date().toISOString().split('T')[0]}.pdf`
                                  );
                                } catch (error) {
                                  console.error('Ошибка при скачивании PDF:', error);
                                  alert('Ошибка при скачивании PDF отчета. Попробуйте еще раз.');
                                } finally {
                                  setIsPdfLoading(false);
                                }
                              }}
                              className="rounded-2xl"
                              label={isPdfLoading ? "Загрузка..." : "Скачать PDF"}
                              size="medium"
                              disabled={isPdfLoading}
                            />
                            <button
                              onClick={() => {
                                const emailData = {
                                  subject: 'Результаты собеседования',
                                  body: `Здравствуйте!\n\nРезультаты собеседования по вакансии.\n\nС уважением,\nHR команда`,
                                  recipients: ['hr@company.com']
                                };
                                const mailtoLink = `mailto:${emailData.recipients.join(',')}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.body)}`;
                                window.open(mailtoLink);
                                console.log("Email functionality executed:", emailData);
                              }}
                              className="box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip px-3 py-2.5 relative rounded-2xl shrink-0 cursor-pointer transition-colors"
                              style={getButtonInlineStyles(colors, 'primary')}
                              onMouseEnter={(e) => { Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary')); }}
                              onMouseLeave={(e) => { Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary')); }}
                            >
                              <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
                                <div className="css-dh74q0 font-inter font-medium leading-[0] not-italic relative shrink-0 text-[#ffffff] text-[14px] text-center text-nowrap">
                                  <p className="block leading-[20px] whitespace-pre">
                                    Отправить письмо
                                  </p>
                                </div>
                              </div>
                            </button>
                          </div>
                        </div>

                        {/* Content */}
                        {contentTab === "candidates" && (
                          <CandidatesTable
                            candidates={candidates}
                            selectedPositionId={selectedVacancyId}
                            candidatesSearchValue={candidatesSearchValue}
                            setCandidatesSearchValue={setCandidatesSearchValue}
                            activeStatusTab={activeStatusTab}
                            setActiveStatusTab={setActiveStatusTab}
                            columnFilters={columnFilters}
                            setColumnFilters={setColumnFilters}
                            localFilters={localFilters}
                            setLocalFilters={setLocalFilters}
                            columnFiltersOpen={columnFiltersOpen}
                            setColumnFiltersOpen={setColumnFiltersOpen}
                            sortDropdownOpen={sortDropdownOpen}
                            setSortDropdownOpen={setSortDropdownOpen}
                            sortFieldsOrder={sortFieldsOrder}
                            setSortFieldsOrder={setSortFieldsOrder}
                            candidatesCurrentPage={candidatesCurrentPage}
                            setCandidatesCurrentPage={setCandidatesCurrentPage}
                            candidatesTotalPages={candidatesTotalPages}
                            pagedCandidates={pagedCandidates}
                            handleCandidateSelect={handleCandidateSelect}
                            handleSelectAllCandidates={handleSelectAllCandidates}
                            handleCandidateClick={handleCandidateClick}
                            areAllCandidatesSelected={areAllCandidatesSelected}
                            draggedItem={draggedItem}
                            setDraggedItem={setDraggedItem}
                            handleDragStart={handleDragStart}
                            handleDragOver={handleDragOver}
                            handleDragEnd={handleDragEnd}
                          />
                        )}

                        {contentTab === "questions" && (
                          <>
                            {console.log('🔍 Rendering QuestionsTab with:', { contentTab, selectedPosition })}
                            <QuestionsTab
                              activeTab={contentTab}
                              onTabChange={setContentTab}
                              selectedPosition={selectedPosition}
                            />
                          </>
                        )}
                      </div>
                    {/* </div> */}
                  {/* </div> */}
                </>
              )}
                </div>
              </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
